import { useAtom } from "jotai";
import type { FC } from "react";
import { useMemo } from "react";

import { selectedAgentIdAtomFamily, selectedDevRunnerIdAtomFamily } from "@/components/ai/chat/state";
import {
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
} from "@/components/podkit/dropdown/DropDown";
import { Tooltip } from "@/components/Tooltip";
import { useFlagAcpAgentsEnabled, useFlagDevRunnerRegistrationEnabled } from "@/hooks/use-flag";
import { Agents } from "@/queries/agent-queries";
import { useListDevRunners } from "@/queries/runner-queries";

type AgentOption = {
    id: string | null;
    label: string;
};

const AGENT_OPTIONS: AgentOption[] = [
    { id: null, label: "Default" },
    { id: Agents.Ona.id, label: "Ona (Runner)" },
    { id: Agents.InEnvironmentOna.id, label: "Ona (Environment)" },
    { id: Agents.InEnvironmentClaudeCode.id, label: "Claude Code" },
    { id: Agents.InEnvironmentCodex.id, label: "Codex" },
    { id: Agents.InEnvironmentGemini.id, label: "Gemini" },
];

/**
 * Renders agent and dev runner selection as sub-menus for use inside a DropdownMenuContent.
 * Each config section is a sub-menu trigger showing the current selection.
 * Gated behind ACP and dev runner feature flags respectively.
 */
export const AgentConfigMenuItems: FC<{ environmentId?: string }> = ({ environmentId }) => {
    const { value: isAcpEnabled } = useFlagAcpAgentsEnabled();
    const { value: isDevRunnerEnabled } = useFlagDevRunnerRegistrationEnabled();
    const { devRunners } = useListDevRunners(isDevRunnerEnabled);

    const agentAtom = useMemo(() => selectedAgentIdAtomFamily(environmentId ?? ""), [environmentId]);
    const devRunnerAtom = useMemo(() => selectedDevRunnerIdAtomFamily(environmentId ?? ""), [environmentId]);
    const [selectedAgentId, setSelectedAgentId] = useAtom(agentAtom);
    const [selectedDevRunnerId, setSelectedDevRunnerId] = useAtom(devRunnerAtom);

    const selectedAgentLabel = AGENT_OPTIONS.find((o) => o.id === selectedAgentId)?.label ?? "Default";
    const showDevRunners = isDevRunnerEnabled && devRunners.length > 0;
    const selectedRunnerLabel = devRunners.find((r) => r.runnerId === selectedDevRunnerId)?.name ?? "Default";

    if (!isAcpEnabled && !showDevRunners) {
        return null;
    }

    return (
        <>
            {isAcpEnabled && (
                <DropdownMenuSub>
                    <Tooltip content={`Agent: ${selectedAgentLabel}`}>
                        <DropdownMenuSubTrigger data-tracking-id="agent-config-agent-submenu">
                            <span className="truncate">Agent: {selectedAgentLabel}</span>
                        </DropdownMenuSubTrigger>
                    </Tooltip>
                    <DropdownMenuSubContent>
                        {AGENT_OPTIONS.map((option) => (
                            <SelectableMenuItem
                                key={option.id ?? "default"}
                                selected={selectedAgentId === option.id}
                                onClick={() => setSelectedAgentId(option.id)}
                                data-tracking-id={`agent-config-${option.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                {option.label}
                            </SelectableMenuItem>
                        ))}
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
            )}

            {showDevRunners && (
                <DropdownMenuSub>
                    <Tooltip content={`Runner: ${selectedRunnerLabel}`}>
                        <DropdownMenuSubTrigger data-tracking-id="agent-config-runner-submenu">
                            <span className="truncate">Runner: {selectedRunnerLabel}</span>
                        </DropdownMenuSubTrigger>
                    </Tooltip>
                    <DropdownMenuSubContent>
                        <SelectableMenuItem
                            selected={!selectedDevRunnerId}
                            onClick={() => setSelectedDevRunnerId(null)}
                            data-tracking-id="agent-config-runner-none"
                        >
                            Default routing
                        </SelectableMenuItem>
                        {devRunners.map((runner) => (
                            <SelectableMenuItem
                                key={runner.runnerId}
                                selected={selectedDevRunnerId === runner.runnerId}
                                onClick={() => setSelectedDevRunnerId(runner.runnerId)}
                                data-tracking-id="agent-config-runner-item"
                            >
                                {runner.name}
                            </SelectableMenuItem>
                        ))}
                    </DropdownMenuSubContent>
                </DropdownMenuSub>
            )}
        </>
    );
};

const SelectableMenuItem: FC<{
    selected: boolean;
    onClick: () => void;
    children: React.ReactNode;
    "data-tracking-id"?: string;
}> = ({ selected, onClick, children, "data-tracking-id": trackingId, ...props }) => (
    <DropdownMenuItem onClick={onClick} data-tracking-id={trackingId ?? "selectable-menu-item"} {...props}>
        <span className="mr-2 inline-block w-2 text-xs">{selected ? "●" : ""}</span>
        {children}
    </DropdownMenuItem>
);
